import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
  Save as SaveIcon,
  CheckCircle as CheckIcon,
  Public as PublicIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import PageHeader from '../../../components/PageHeader';
import calendarApi, {
  AvailabilityRule,
  BlockedDate,
  BookingType,
  CalendarSettings,
} from './calendarApi';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface UserOpt {
  user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

const CalendarSettingsPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // General
  const [settings, setSettings] = useState<CalendarSettings | null>(null);
  const [slugInput, setSlugInput] = useState('');
  const [slugStatus, setSlugStatus] = useState<'available' | 'taken' | 'reserved' | 'invalid' | null>(null);
  const [newPublicEmail, setNewPublicEmail] = useState('');

  // Availability
  const [rules, setRules] = useState<AvailabilityRule[]>([]);

  // Blocked dates
  const [blocks, setBlocks] = useState<BlockedDate[]>([]);
  const [blockStart, setBlockStart] = useState<Dayjs | null>(null);
  const [blockEnd, setBlockEnd] = useState<Dayjs | null>(null);
  const [blockReason, setBlockReason] = useState('');

  // Booking types
  const [types, setTypes] = useState<BookingType[]>([]);
  const [newType, setNewType] = useState<Partial<BookingType>>({
    name: '', color: '#2563eb', default_duration_minutes: 30, is_public_bookable: false,
  });

  // Public notification users
  const [allUsers, setAllUsers] = useState<UserOpt[]>([]);
  const [selectedNotifyUsers, setSelectedNotifyUsers] = useState<UserOpt[]>([]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r, b, t, pu] = await Promise.all([
        calendarApi.getSettings(),
        calendarApi.getAvailabilityRules(),
        calendarApi.getBlockedDates(),
        calendarApi.getBookingTypes(),
        calendarApi.getPublicNotificationUsers(),
      ]);
      setSettings({
        ...s,
        allowSameDayBooking: s.allowSameDayBooking ?? true,
        minNoticeMinutes: s.minNoticeMinutes ?? 60,
      });
      setSlugInput(s.businessSlug);
      setRules(r);
      setBlocks(b);
      setTypes(t);
      setSelectedNotifyUsers(pu);
    } catch (e: any) {
      setError(e.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE_URL}/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const arr = Array.isArray(r.data) ? r.data : (r.data?.users || []);
        setAllUsers(arr.map((u: any) => ({
          user_id: u.user_id || u.id,
          email: u.email,
          first_name: u.first_name,
          last_name: u.last_name,
        })));
      })
      .catch(() => setAllUsers([]));
  }, []);

  // Slug availability check (debounced)
  useEffect(() => {
    if (!settings || slugInput === settings.businessSlug) { setSlugStatus(null); return; }
    if (!slugInput.trim()) { setSlugStatus('invalid'); return; }
    const handle = setTimeout(async () => {
      try {
        const r = await calendarApi.checkSlugAvailable(slugInput);
        setSlugStatus(r.available ? 'available' : (r.reason as any) || 'taken');
      } catch (_) { setSlugStatus(null); }
    }, 400);
    return () => clearTimeout(handle);
  }, [slugInput, settings]);

  const saveSettings = useCallback(async (patch: Partial<CalendarSettings>) => {
    setSaving(true); setError(null); setInfo(null);
    try {
      await calendarApi.updateSettings(patch);
      setInfo('Settings saved');
      await refreshAll();
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [refreshAll]);

  if (loading || !settings) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const publicUrl = `${window.location.origin}/${settings.businessSlug}/booking`;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <PageHeader
          title="Calendar settings"
          subtitle="Configure your public booking page, working hours, blocked dates and booking types"
        />

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

        <Card sx={{ borderRadius: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
            <Tab label="General" />
            <Tab label="Availability" />
            <Tab label="Blocked dates" />
            <Tab label="Booking types" />
            <Tab label="Public notifications" />
          </Tabs>
          <Divider />

          <CardContent>
            {tab === 0 && (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>Public booking page</Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.publicBookingEnabled}
                        onChange={(e) => saveSettings({ publicBookingEnabled: e.target.checked })}
                      />
                    }
                    label="Allow visitors to book online"
                  />
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Public URL slug"
                    size="small"
                    value={slugInput}
                    onChange={(e) => setSlugInput(e.target.value.toLowerCase())}
                    helperText={`Your public page lives at ${window.location.host}/${slugInput || '…'}/booking`}
                    InputProps={{
                      endAdornment: slugStatus && (
                        <InputAdornment position="end">
                          {slugStatus === 'available' && <CheckIcon color="success" fontSize="small" />}
                          {slugStatus !== 'available' && (
                            <Tooltip title={slugStatus}><BlockIcon color="error" fontSize="small" /></Tooltip>
                          )}
                        </InputAdornment>
                      ),
                    }}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={saving || slugInput === settings.businessSlug || slugStatus === 'taken' || slugStatus === 'reserved' || slugStatus === 'invalid'}
                    onClick={() => saveSettings({ businessSlug: slugInput })}
                  >
                    Save slug
                  </Button>
                </Stack>

                {settings.publicBookingEnabled && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 2, bgcolor: '#f1f5f9', borderRadius: 2 }}>
                    <PublicIcon color="primary" />
                    <Typography sx={{ flex: 1, fontFamily: 'monospace' }}>{publicUrl}</Typography>
                    <IconButton size="small" onClick={() => { navigator.clipboard.writeText(publicUrl); setInfo('Copied to clipboard'); }}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <Button size="small" onClick={() => window.open(publicUrl, '_blank')}>Open</Button>
                  </Stack>
                )}

                <Divider />

                <Typography variant="h6">Slot rules</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Timezone"
                    size="small"
                    value={settings.timezone}
                    onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                    onBlur={() => saveSettings({ timezone: settings.timezone })}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Slot duration (min)"
                    size="small" type="number"
                    value={settings.slotDurationMinutes}
                    onChange={(e) => setSettings({ ...settings, slotDurationMinutes: Number(e.target.value) })}
                    onBlur={() => saveSettings({ slotDurationMinutes: settings.slotDurationMinutes })}
                    sx={{ width: 200 }}
                  />
                  <TextField
                    label="Buffer (min)"
                    size="small" type="number"
                    value={settings.bufferMinutes}
                    onChange={(e) => setSettings({ ...settings, bufferMinutes: Number(e.target.value) })}
                    onBlur={() => saveSettings({ bufferMinutes: settings.bufferMinutes })}
                    sx={{ width: 160 }}
                  />
                </Stack>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={settings.allowSameDayBooking ?? true}
                      onChange={(_, checked) => {
                        setSettings({ ...settings, allowSameDayBooking: checked });
                        saveSettings({ allowSameDayBooking: checked });
                      }}
                    />
                  )}
                  label="Allow same-day bookings"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 1 }}>
                  {settings.allowSameDayBooking
                    ? 'Visitors can book today. Slots start after the minimum lead time below (rounded to your slot length).'
                    : 'Today is hidden on the public calendar. Bookings open from tomorrow, or after the minimum notice in hours.'}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  {settings.allowSameDayBooking ? (
                    <TextField
                      label="Minimum lead time (minutes)"
                      size="small"
                      type="number"
                      inputProps={{ min: 0, step: 5 }}
                      value={settings.minNoticeMinutes}
                      onChange={(e) => setSettings({ ...settings, minNoticeMinutes: Number(e.target.value) })}
                      onBlur={() => saveSettings({ minNoticeMinutes: settings.minNoticeMinutes })}
                      sx={{ flex: 1 }}
                      helperText="e.g. 60 = next slot at least 1 hour from now"
                    />
                  ) : (
                    <TextField
                      label="Minimum notice (hours)"
                      size="small"
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      value={settings.minNoticeHours}
                      onChange={(e) => setSettings({ ...settings, minNoticeHours: Number(e.target.value) })}
                      onBlur={() => saveSettings({ minNoticeHours: settings.minNoticeHours })}
                      sx={{ flex: 1 }}
                      helperText="Used when same-day booking is off"
                    />
                  )}
                  <TextField
                    label="Max advance (days)"
                    size="small" type="number"
                    value={settings.maxAdvanceDays}
                    onChange={(e) => setSettings({ ...settings, maxAdvanceDays: Number(e.target.value) })}
                    onBlur={() => saveSettings({ maxAdvanceDays: settings.maxAdvanceDays })}
                    sx={{ flex: 1 }}
                  />
                </Stack>

                <TextField
                  label="Public page intro"
                  multiline rows={3}
                  value={settings.publicBookingIntro || ''}
                  onChange={(e) => setSettings({ ...settings, publicBookingIntro: e.target.value })}
                  onBlur={() => saveSettings({ publicBookingIntro: settings.publicBookingIntro })}
                />
                <TextField
                  label="Logo URL (optional)"
                  size="small"
                  value={settings.publicBookingLogoUrl || ''}
                  onChange={(e) => setSettings({ ...settings, publicBookingLogoUrl: e.target.value })}
                  onBlur={() => saveSettings({ publicBookingLogoUrl: settings.publicBookingLogoUrl })}
                />
              </Stack>
            )}

            {tab === 1 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Recurring weekly working hours. Visitors can only pick slots that fall within these windows.
                </Typography>
                <Stack spacing={1}>
                  {DAY_NAMES.map((name, dow) => {
                    const dayRules = rules.filter((r) => Number(r.day_of_week) === dow);
                    return (
                      <Stack
                        key={dow}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ borderBottom: '1px solid #f1f5f9', py: 1 }}
                      >
                        <Typography sx={{ width: 100, fontWeight: 600 }}>{name}</Typography>
                        <Stack direction="row" spacing={1} sx={{ flex: 1, flexWrap: 'wrap', gap: 1 }}>
                          {dayRules.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">Closed</Typography>
                          ) : dayRules.map((r, idx) => (
                            <Chip
                              key={idx}
                              label={`${r.start_time?.slice(0, 5)} – ${r.end_time?.slice(0, 5)}`}
                              onDelete={() => setRules((all) => all.filter((x) => x !== r))}
                            />
                          ))}
                        </Stack>
                        <Button
                          size="small"
                          onClick={() => setRules((all) => [...all, { day_of_week: dow, start_time: '09:00:00', end_time: '17:00:00' }])}
                        >
                          + Add slot
                        </Button>
                      </Stack>
                    );
                  })}
                </Stack>
                <Box sx={{ mt: 3, textAlign: 'right' }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={async () => {
                      setSaving(true); setError(null);
                      try { await calendarApi.replaceAvailabilityRules(rules); setInfo('Availability saved'); }
                      catch (e: any) { setError(e.message); } finally { setSaving(false); }
                    }}
                  >
                    Save availability
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Times use the business timezone (currently <strong>{settings.timezone}</strong>).
                </Typography>
              </Box>
            )}

            {tab === 2 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Mark holiday closures, training days, or any window where visitors should not be able to book.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                  <DateTimePicker label="From" value={blockStart} onChange={setBlockStart} slotProps={{ textField: { size: 'small' } }} />
                  <DateTimePicker label="To" value={blockEnd} onChange={setBlockEnd} slotProps={{ textField: { size: 'small' } }} />
                  <TextField label="Reason" size="small" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} sx={{ flex: 1 }} />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    disabled={!blockStart || !blockEnd}
                    onClick={async () => {
                      try {
                        await calendarApi.addBlockedDate({
                          start_at: blockStart!.toISOString(),
                          end_at: blockEnd!.toISOString(),
                          reason: blockReason || undefined,
                        });
                        setBlockStart(null); setBlockEnd(null); setBlockReason('');
                        const next = await calendarApi.getBlockedDates();
                        setBlocks(next);
                      } catch (e: any) { setError(e.message); }
                    }}
                  >
                    Block
                  </Button>
                </Stack>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell width={50} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {blocks.length === 0 && (
                      <TableRow><TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary">No blocked dates</Typography>
                      </TableCell></TableRow>
                    )}
                    {blocks.map((b) => (
                      <TableRow key={b.block_id}>
                        <TableCell>{dayjs(b.start_at).format('DD MMM YYYY HH:mm')}</TableCell>
                        <TableCell>{dayjs(b.end_at).format('DD MMM YYYY HH:mm')}</TableCell>
                        <TableCell>{b.reason || ''}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={async () => {
                            await calendarApi.deleteBlockedDate(b.block_id);
                            setBlocks((all) => all.filter((x) => x.block_id !== b.block_id));
                          }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {tab === 3 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Booking types are how you categorise events on the calendar. Mark a type as <strong>publicly bookable</strong> to expose it on the public page.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
                  <TextField label="Name" size="small" value={newType.name || ''} onChange={(e) => setNewType({ ...newType, name: e.target.value })} sx={{ flex: 1 }} />
                  <TextField label="Color" size="small" type="color" value={newType.color || '#2563eb'} onChange={(e) => setNewType({ ...newType, color: e.target.value })} sx={{ width: 120 }} />
                  <TextField label="Duration (min)" size="small" type="number" value={newType.default_duration_minutes || 30} onChange={(e) => setNewType({ ...newType, default_duration_minutes: Number(e.target.value) })} sx={{ width: 140 }} />
                  <FormControlLabel
                    control={<Switch checked={!!newType.is_public_bookable} onChange={(e) => setNewType({ ...newType, is_public_bookable: e.target.checked })} />}
                    label="Public"
                  />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    disabled={!newType.name?.trim()}
                    onClick={async () => {
                      try {
                        await calendarApi.createBookingType(newType);
                        setNewType({ name: '', color: '#2563eb', default_duration_minutes: 30, is_public_bookable: false });
                        setTypes(await calendarApi.getBookingTypes());
                      } catch (e: any) { setError(e.response?.data?.message || e.message); }
                    }}
                  >
                    Add type
                  </Button>
                </Stack>

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Color</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Default duration</TableCell>
                      <TableCell>Public</TableCell>
                      <TableCell>Active</TableCell>
                      <TableCell width={50} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {types.map((t) => (
                      <TableRow key={t.type_id}>
                        <TableCell>
                          <input
                            type="color"
                            value={t.color}
                            style={{ width: 32, height: 24, border: 0, background: 'transparent' }}
                            onChange={async (e) => {
                              await calendarApi.updateBookingType(t.type_id, { color: e.target.value });
                              setTypes(await calendarApi.getBookingTypes());
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small" variant="standard"
                            defaultValue={t.name}
                            onBlur={async (e) => {
                              if (e.target.value && e.target.value !== t.name) {
                                await calendarApi.updateBookingType(t.type_id, { name: e.target.value });
                                setTypes(await calendarApi.getBookingTypes());
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small" variant="standard" type="number"
                            defaultValue={t.default_duration_minutes}
                            onBlur={async (e) => {
                              const v = Number(e.target.value);
                              if (v > 0 && v !== t.default_duration_minutes) {
                                await calendarApi.updateBookingType(t.type_id, { default_duration_minutes: v });
                                setTypes(await calendarApi.getBookingTypes());
                              }
                            }}
                            sx={{ width: 90 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Switch
                            size="small"
                            checked={!!t.is_public_bookable}
                            onChange={async (e) => {
                              await calendarApi.updateBookingType(t.type_id, { is_public_bookable: e.target.checked });
                              setTypes(await calendarApi.getBookingTypes());
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Switch
                            size="small"
                            checked={!!t.is_active}
                            onChange={async (e) => {
                              await calendarApi.updateBookingType(t.type_id, { is_active: e.target.checked });
                              setTypes(await calendarApi.getBookingTypes());
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={async () => {
                            if (!window.confirm('Deactivate this booking type?')) return;
                            await calendarApi.deleteBookingType(t.type_id);
                            setTypes(await calendarApi.getBookingTypes());
                          }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {tab === 4 && (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="h6">Internal users notified on public bookings</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    When someone books through your public page, every user selected here receives an in-app notification, email, and push notification (if they have the mobile app).
                  </Typography>
                  <Autocomplete
                    multiple
                    size="small"
                    options={allUsers}
                    getOptionLabel={(u) => `${[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email} (${u.email})`}
                    value={selectedNotifyUsers}
                    onChange={(_, v) => setSelectedNotifyUsers(v as UserOpt[])}
                    renderInput={(p) => <TextField {...p} label="Notify these users" placeholder="Add staff…" />}
                    isOptionEqualToValue={(a, b) => a.user_id === b.user_id}
                  />
                  <Box sx={{ mt: 2, textAlign: 'right' }}>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={async () => {
                        try {
                          await calendarApi.setPublicNotificationUsers(selectedNotifyUsers.map((u) => u.user_id));
                          setInfo('Notification list saved');
                        } catch (e: any) { setError(e.message); }
                      }}
                    >
                      Save list
                    </Button>
                  </Box>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="h6">External emails</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Extra email addresses to notify on every public booking (e.g. <em>sales@example.com</em>). These do not need to be users.
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <TextField
                      size="small" label="Add email"
                      value={newPublicEmail}
                      onChange={(e) => setNewPublicEmail(e.target.value)}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => {
                        if (!newPublicEmail.trim()) return;
                        const next = [...(settings.publicNotificationEmails || []), newPublicEmail.trim()];
                        setSettings({ ...settings, publicNotificationEmails: next });
                        saveSettings({ publicNotificationEmails: next });
                        setNewPublicEmail('');
                      }}
                    >Add</Button>
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {(settings.publicNotificationEmails || []).map((e) => (
                      <Chip
                        key={e}
                        label={e}
                        onDelete={() => {
                          const next = (settings.publicNotificationEmails || []).filter((x) => x !== e);
                          setSettings({ ...settings, publicNotificationEmails: next });
                          saveSettings({ publicNotificationEmails: next });
                        }}
                      />
                    ))}
                    {(settings.publicNotificationEmails || []).length === 0 && (
                      <Typography variant="body2" color="text.secondary">No external emails configured</Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Container>
    </LocalizationProvider>
  );
};

export default CalendarSettingsPage;
